/**
 * Returns a throttled version of the given function.
 *
 * As long as the returned function continues to be invoked, the original
 * function will not be called. Once the returned function stops being invoked
 * for `period` milliseconds, the original function will be called.
 *
 * If `options.immediate` is true, the returned function will be triggered on
 * the leading edge instead of trailing.
 *
 * Original source taken from:
 * https://davidwalsh.name/javascript-debounce-function
 *
 * @param {Function} fn
 * @param {object|number} options - can pass period instead of object
 * @param {number}        options.period - duration in milliseconds
 * @param {boolean}       options.immediate
 */
export function debounce(fn, options) {

    if (typeof options === 'number') {
        options = {
            period: options,
        };
    }
    options = Object.assign({
        period: 0,
        immediate: false,
    }, options);

    let timeout;
    return function() {
        const context = this;
        const args = arguments;
        const later = function() {
            timeout = null;
            if (!options.immediate) {
                fn.apply(context, args);
            }
        };
        const callNow = options.immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, options.period);
        if (callNow) {
            fn.apply(context, args);
        }
    };
}

/**
 * Returns a throttled version of the given function.
 *
 * You can call the returned function as many times as you like, but the
 * original function will only be called once per `period` milliseconds.
 *
 * If `followUp` option is true, then a follow-up call will automatically be
 * made when the period expires. For example:
 *  - assume period is 200ms
 *  - throttled func is called @ 10ms ->
 *      call goes through
 *  - throttled func is called @ 20ms ->
 *      call is throttled, but follow up is scheduled
 *  - throttled func is called @ 30ms ->
 *      call is throttled; follow up is already scheduled, so no change there
 *  - nothing else happens ->
 *      scheduled call is automatically sent @ 210ms
 *
 * @param {Function}      fn
 * @param {object|number} options - can pass period instead of object
 * @param {number}        options.period - duration in milliseconds between calls
 * @param {boolean}       options.[followUp=false] - see above
 */
export function throttleFunc(fn, options) {
    let period = 0;
    let followUp = false;

    if (typeof options === 'number') {
        period = options;
    } else {
        period = options.period;
        followUp = !!options.followUp;
    }

    let throttler = new Throttler({ period });
    let timeout = null;

    return function(...args) {
        function makeCall() {
            throttler.recordCall();
            fn(...args);
        }

        if (throttler.canCall()) {
            makeCall();
        } else {
            const callScheduled = !!timeout;
            const timeLeft = throttler.getTimeUntilNextCall();

            if (followUp && !callScheduled) {
                timeout = setTimeout(() => {
                    makeCall();
                    timeout = null;
                }, timeLeft);
            }
        }
    }
}

class Throttler {
    /**
     * @param {object} options
     * @param {number} options.period - in milliseconds
     */
    constructor(options) {
        this.period = options.period;
        this._lastCall = null;
    }

    async wait() {
        if (this._lastCall === null) {
            return;
        }
        const timeLeft = this.getTimeUntilNextCall();
        if (timeLeft > 0) {
            await sleep(timeLeft);
        }
    }

    getTimeUntilNextCall() {
        const now = (new Date).getTime();
        const timeElapsed = now - this._lastCall;
        const timeLeft = this.period - timeElapsed;
        return timeLeft;
    }

    canCall() {
        const timeLeft = this.getTimeUntilNextCall();
        return timeLeft <= 0;
    }

    recordCall() {
        const now = (new Date).getTime();
        this._lastCall = now;
    }
}

export default {
    debounce,
    throttleFunc,
    Throttler,
};

async function sleep(ms) {
    return new Promise(r => setTimeout(r, ms));
}



// WEBPACK FOOTER //
// ./src/utils/rate.js